import {Tr} from "@/i18n/i18n";
import {injectable} from "inversify";
import {BalanceListener, Listener} from "@/store/listener";
import ListenerManager from "@/managers/listener-manager";
import {AccessLevel, CampaignType, InterfaceMode, OfferType, Player, PlayerShortInfo} from "@/store/player/types";
import {Keys} from "@/constants/key";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {Creature} from "@/store/level/types";
import {SceneName} from "@/model/scene-name";
import {MenuNotification} from "@/scenes.ui/menu-notification";
import {NewTaskFeedback} from "@/scenes.ui/new-task-feedback";
import {CreatureUtils} from "@/utils/creature-utils";
import CU from "../utils/c-u";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Point} from "@/model/point";
import {MenuCreaturesNavPanel} from "@/scenes.ui/menu-creatures-nav-panel";
import {CreatureInteractionEffectTypePanel} from "@/scenes.ui/creature-interaction-effect-type-panel";
import OnceCaller from "@/utils/once-caller";
import {Settings} from "@/constants/settings";
import SceneUtils from "@/utils/scene-utils";
import TimeUtils from "@/utils/time-utils";
import ClanUtils from "@/utils/clan-utils";
import PlayerStore from "@/store/player";
import TaskStore from "@/store/task";
import {GameState} from "@/constants/game-state";
import {ResPanel} from "@/scenes.ui/res-panel";
import {ResourceContainer} from "@/scenes.ui/resource-container";
import {BottomPanel} from "@/scenes.ui/bottom-panel";
import {Version} from "@/constants/version";
import {InfoPanel} from "@/scenes.ui/info-panel";
import {Positioner} from "@/utils/positioner";
import {PositionUtils} from "@/utils/position-utils";
import SpriteUtils from "@/utils/sprite-utils";
import {ResourceType, SkillType} from "@/store/place/types";
import {TaskService} from "@/services/task-service";
import {ResourceAddEffectPanel} from "@/scenes.ui/resource-add-effect-panel";
import RoadScene from "@/scenes/roadScene";
import PlayerUtils from "@/utils/player-utils";
import {PowerUpUtils} from "@/utils/power-up-utils";
import PowerUpPanel from "@/scenes.ui/power-up-panel";
import {StringUtils} from "@/utils/string-utils";
import {IntegrationUtils} from "@/utils/integration-utils";
import StatPanelUtils from "@/utils/stat-panel-utils";
import {ChainUtils} from "@/utils/chain-utils";
import {Chain, ChainState} from "@/store/chain/types";
import ChainStore from "@/store/chain";
import {HintDto} from "@/store/task/types";
import {TizerPanel} from "@/scenes.ui/tizer-panel";
import {Sale} from "@/store/sale/types";
import {SaleButtonMode, SaleUtils} from "@/utils/sale-utils";
import {MenuColumn, MenuUtils} from "@/utils/menu-utils";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import Sprite = Phaser.GameObjects.Sprite;
import Image = Phaser.GameObjects.Image;
import Text = Phaser.GameObjects.Text;
import SecurityStore from "@/store/security";
import {TwoOptionsPanel} from "@/scenes.ui/two-options-panel";
import {Http} from "@/constants/http";
import {YaStorage} from "@/utils/ya-storage";

@injectable()
export class MenuService implements Listener, BalanceListener {

    public static readonly DEFAULT_DEPTH: number = 1000;

    private scene: Scene | undefined;
    private canvas: HTMLCanvasElement | undefined;
    private resContainer: ResourceContainer | undefined;

    private bottomPanel!: BottomPanel;
    private resPanel: ResPanel | undefined;

    private withCreatures: boolean = true;

    private creaturePanelById: Map<number, CreaturePanel> = new Map<number, CreaturePanel>();

    private startSceneCallback: ((sceneName: SceneName, params: any) => void) | undefined;
    private openShopCallback!: () => unknown;
    private openBankCallback!: () => unknown;
    private openArenaCallback!: () => unknown;
    private openClanCallback!: () => unknown;
    private openHelpCallback!: () => unknown;
    private openTasksCallback!: () => unknown;
    private openProfileCallback!: () => unknown;
    private openForumCallback!: () => unknown;
    private openChainCallback!: (chainId: number, chainCont: Container) => unknown;
    private openSaleCallback!: (saleId: string) => unknown;
    private openYaAccCallback!: (cont: Container) => unknown;

    private mainContainer!: Container;

    private registerButtonContainer!: Container;
    private shopButtonContainer!: Container;
    private bankButtonContainer!: Container;
    private arenaButtonContainer!: Container;
    private clanButtonContainer!: Container;
    private hintButtonContainer!: Container;
    private powerUpPanel!: PowerUpPanel;
    private tasksButtonContainer!: Container;
    private chatButtonContainer!: Container;
    private reloadButtonContainer!: Container;
    private mailButtonContainer!: Container;
    private leaderboardButtonContainer!: Container;
    private profileButtonContainer!: Container;
    private forumButtonContainer!: Container;
    private chainContainers: Container[] = [];
    private saleButtonContainer!: Container;
    private yaRemapContainer!: Container;

    private taskDoneIcon!: Sprite;
    private newBankSign!: MenuNotification;
    private newTaskSign!: MenuNotification;
    private newBadgeSign!: MenuNotification;
    private newMailSign!: MenuNotification;
    private newChatSign!: MenuNotification;
    private arenaSign!: MenuNotification;
    private clanSign!: MenuNotification;
    private hintSign!: MenuNotification;
    private forumSign!: MenuNotification;
    private chainSigns: MenuNotification[] = [];

    private firstNonTemporalCreaturePos!: number;

    private nonTemporalCreaturesList!: Creature[];

    private profileButton!: Image;
    private shopButton!: Image;
    private bankButton!: Image;
    private arenaButton!: Image;
    private clanButton!: Image;
    private hintButton!: Image;
    private leaderboardButton!: Image;
    private chatButton!: Image;
    private reloadButton!: Image;
    private mailButton!: Image;
    private tasksButton!: Image;
    private registerButtonRibbon: Image | undefined;
    private forumButton!: Image;
    private chainButtons: Image[] = [];
    private saleButton!: Image;
    private yaRemapButton!: Image;

    public menuNavPanel!: MenuCreaturesNavPanel;

    private showNavPanel: boolean = true;

    private pos!: Positioner;

    private chainTimerIds: number[] = [];

    public createMenu(scene: Scene, canvas: HTMLCanvasElement, withCreatures: boolean,
                      startSceneCallback: (sceneName: SceneName, params: any) => void, openShopCallback: () => unknown,
                      openBankCallback: () => unknown,
                      openTasksCallback: () => unknown,  openProfileCallback: () => unknown,
                      openArenaCallback: () => unknown, openClanCallback: () => unknown,
                      openForumCallback: () => unknown, openHelpCallback: () => unknown,
                      openChainCallback: (chainId: number, chainCont: Container) => unknown,
                      openSaleCallback: (saleId: string) => unknown,
                      openYaAccCallback: (cont: Container) => unknown): Container {

        this.scene = scene;
        this.canvas = canvas;
        this.startSceneCallback = startSceneCallback;
        this.openShopCallback = openShopCallback;
        this.openBankCallback = openBankCallback;
        this.openBankCallback = openBankCallback;
        this.openArenaCallback = openArenaCallback;
        this.openClanCallback = openClanCallback;
        this.openHelpCallback = openHelpCallback;
        this.openTasksCallback = openTasksCallback;
        this.openProfileCallback = openProfileCallback;
        this.openForumCallback = openForumCallback;
        this.openChainCallback = openChainCallback;
        this.openSaleCallback = openSaleCallback;
        this.openYaAccCallback = openYaAccCallback;
        this.withCreatures = withCreatures;
        this.pos = PositionUtils.getPositioner();

        let player = PlayerStore.getPlayer();

        this.creaturePanelById.clear();

        this.mainContainer = this.createContainer(scene);
        this.tasksButtonContainer = this.createContainer(scene);
        this.shopButtonContainer = this.createContainer(scene);
        this.bankButtonContainer = this.createContainer(scene);
        this.arenaButtonContainer = this.createContainer(scene);
        this.clanButtonContainer = this.createContainer(scene);
        this.hintButtonContainer = this.createContainer(scene);
        this.chatButtonContainer = this.createContainer(scene);
        this.reloadButtonContainer = this.createContainer(scene);
        this.mailButtonContainer = this.createContainer(scene);
        this.leaderboardButtonContainer = this.createContainer(scene);
        this.profileButtonContainer = this.createContainer(scene);
        this.registerButtonContainer = this.createContainer(scene);
        this.forumButtonContainer = this.createContainer(scene);
        this.saleButtonContainer = this.createContainer(scene);
        this.yaRemapContainer = this.createContainer(scene);

        SpriteUtils.addSprite(this.scene!, 0, 0, Keys.ATLAS_UI, Keys.UI_MENU_BACK)
            .setOrigin(0, 0);

        this.bottomPanel = new BottomPanel(scene, 0, canvas.height, player);
        this.mainContainer.add(this.bottomPanel);

        switch (player.interfaceMode) {
            case InterfaceMode.ORIGINAL:
                this.resourceToBottom();
                break;
            case InterfaceMode.MODERN:
                this.resPanel = new ResPanel(scene, 0, canvas.height, player);
                this.mainContainer.add(this.resPanel);
                this.resourceToSide();
                break;
        }

        ListenerManager.addPlayerListener(this);
        ListenerManager.addBalanceListener(this);
        ListenerManager.setUpdateHintCallback(() => this.updateHint());

        this.createButtons(scene, canvas);

        if (withCreatures) {
            let creatures: Creature[] = CreatureUtils.sortCreaturesDescending(player.creatures);
            this.nonTemporalCreaturesList = CreatureUtils.findNonTemporalCreatures(creatures)
            this.firstNonTemporalCreaturePos = this.nonTemporalCreaturesList.findIndex(c => !c.temporal);

            this.addCreaturesNavPanel(creatures);
        }

        return this.mainContainer;
    }

    public resourceToSide() {
        if (this.resPanel) {
            this.resContainer = this.resPanel;
            this.bottomPanel.setVisible(false);
            this.resContainer.setVisible(true);
            this.resContainer!.setBalance(PlayerStore.getPlayer());
        }
    }

    public resourceToBottom() {
        if (this.resPanel) {
            this.resPanel.setVisible(false);
        }
        if (this.bottomPanel) {
            let self = this;
            this.resContainer = this.bottomPanel;
            this.resContainer.setVisible(true);
            this.resContainer.setAlpha(0);
            this.scene!.tweens.add({
                targets: this.resContainer,
                alpha: 1,
                delay: 300,
                duration: 300,
                onComplete() {
                    self.resContainer!.setAlpha(1);

                }
            });
            this.resContainer!.setBalance(PlayerStore.getPlayer());
        }
    }

    public destroyMenu() {
        this.mainContainer.destroy();
        this.menuNavPanel.destroy();
        this.tasksButtonContainer.destroy();
        this.shopButtonContainer.destroy();
        this.bankButtonContainer.destroy();
        this.arenaButtonContainer.destroy();
        this.clanButtonContainer.destroy();
        this.hintButtonContainer.destroy();
        this.powerUpPanel.destroy();
        this.chatButtonContainer.destroy();
        this.reloadButtonContainer.destroy();
        this.mailButtonContainer.destroy();
        this.leaderboardButtonContainer.destroy();
        this.profileButtonContainer.destroy();
        this.registerButtonContainer.destroy();
        this.forumButtonContainer.destroy();
        this.saleButtonContainer.destroy();
        this.yaRemapContainer.destroy();
        this.destroyChainButtons();
    }

    public destroyChainButtons() {
        for (let c of this.chainContainers) {
            c.destroy();
        }
    }

    public getCreaturePanelPosition(creatureId: number): Point | undefined {
        let cp: CreaturePanel | undefined = this.creaturePanelById.get(creatureId);
        if (cp) {
            return new Point(cp.getBounds().left - 70, cp.getBounds().top - 10);
        }
        return undefined;
    }

    private addCreaturesNavPanel(creatures: Creature[]) {
        creatures = creatures.filter(c => c.combative);

        if (this.menuNavPanel) { this.menuNavPanel.destroy() }

        this.showNavPanel = true;

        let withArrows = creatures.length > MenuCreaturesNavPanel.MAX_CREATURES_NO_ARROWS;
        let maxCreatures = withArrows ? 4 : MenuCreaturesNavPanel.MAX_CREATURES_NO_ARROWS;
        let calcX = withArrows ? 430 : this.canvas!.width - creatures.length * 30 * CU.S;
        calcX += creatures.length == MenuCreaturesNavPanel.MAX_CREATURES_NO_ARROWS ? 17 : 0;

        let self = this;
        this.menuNavPanel = new MenuCreaturesNavPanel(this.scene!, this.canvas!, calcX - this.pos.getButtonsRightX(), this.canvas!.height - 80 * CU.S - this.pos.getButtonsBottomY() * CU.S,
            creatures, (sceneName: SceneName, { creatureIndex: creatureIndex} ) => {
                if(!StatPanelUtils.isCreatureStatPanelVisible()) {
                    OnceCaller.doOnce("toCreatureScene", () => {
                        self.startSceneCallback!(SceneName.CREATURE_SCENE, {creatureIndex: creatureIndex});
                    });
                }
            }, maxCreatures);

        this.menuNavPanel.setDepth(this.mainContainer.depth + 1);
        this.menuNavPanel.name = "menuNavPanel";

        this.scene!.add.existing(this.menuNavPanel);

        this.fillCreaturePanelByIdMap(<CreaturePanel[]>this.menuNavPanel.getNavContainer().getAll());

    }

    private fillCreaturePanelByIdMap(creaturePanels: CreaturePanel[]) {
        creaturePanels.forEach(cp => {
            this.creaturePanelById.set(cp.getCreature().id, cp);
        });
    }

    private createContainer(scene: Scene): Container {
        let cont: Container = new Container(scene);
        scene.add.existing(cont);
        cont.setDepth(MenuService.DEFAULT_DEPTH);
        return cont;
    }

    private addButton(xModern: number, yModern: number,
                      xOrig: number, yOrig: number,
                      mode: InterfaceMode,
                      modernCol: MenuColumn, origCol: MenuColumn,
                      name: string, atlasName: string | undefined, spriteName: string,
                      container: Container, textShift: number = 35): [Sprite, Text] {
        let sprite;

        if (atlasName) {
            sprite = SpriteUtils.addSpriteN(this.scene!, mode === InterfaceMode.ORIGINAL ? xOrig : xModern,
                mode === InterfaceMode.ORIGINAL ? yOrig : yModern, atlasName, spriteName);
        } else {
            sprite = SpriteUtils.addSpriteN(this.scene!, mode === InterfaceMode.ORIGINAL ? xOrig : xModern,
                mode === InterfaceMode.ORIGINAL ? yOrig : yModern, spriteName);
        }

        switch (mode) {
            case InterfaceMode.MODERN:
                if (modernCol === MenuColumn.LEFT) {
                    MenuUtils.LEFT_Y_CURR = Math.max(MenuUtils.LEFT_Y_CURR, yModern / CU.S);
                } else if (modernCol === MenuColumn.RIGHT) {
                    MenuUtils.RIGHT_Y_CURR = Math.max(MenuUtils.RIGHT_Y_CURR, yModern / CU.S);
                }
                break;
            case InterfaceMode.ORIGINAL:
                if (origCol === MenuColumn.LEFT) {
                    MenuUtils.LEFT_Y_CURR = Math.max(MenuUtils.LEFT_Y_CURR, yOrig / CU.S);
                } else if (origCol === MenuColumn.RIGHT) {
                    MenuUtils.RIGHT_Y_CURR = Math.max(MenuUtils.RIGHT_Y_CURR, yOrig / CU.S);
                }
                break;
        }

        let buttonTitle = TextUtils.addTextAdvanced(this.scene!, name, sprite.x / CU.S, sprite.y / CU.S + textShift,
            '#ffffff', 7.7, 'normal', 74, 'center', 0, Fonts.MYRIADPRO_BOLD);
        buttonTitle.setStroke('#000000', 3);
        container.add([sprite, buttonTitle]);
        return [sprite, buttonTitle];
    }

    private createButtons(scene: Scene, canvas: HTMLCanvasElement): void {
        let player: Player = PlayerStore.getPlayer();
        let mode = player.interfaceMode;

        let campaignTypes: CampaignType[] = PlayerStore.getCampaignTypes();
        let offer = player.offer;
        let sideRibbonsCount = (campaignTypes ? campaignTypes.length : 0)
            + (!!offer && !!offer.offerName && (offer.offerType !== OfferType.MYTH_10 && offer.offerType !== OfferType.MYTH_20 && offer.offerType !== OfferType.MYTH_25 && offer.offerType !== OfferType.MYTH_50 && offer.offerType !== OfferType.MYTH_75) ? 1 : 0)
            + (player.accessLevel === AccessLevel.TRIAL ? 0.8 : 0);

        let p = this.pos;

        this.profileButton = this.addButton(CU.shiftLeft(34 + p.getButtonsLeftX()), CU.shiftBottom(canvas, 49 + p.getButtonsBottomY()),
            CU.shiftLeft(34), CU.shiftTop(34),
            mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('profil'),
            Keys.ATLAS_UI_2, Keys.UI2_PROFILE, this.profileButtonContainer, 40)[0];

        this.shopButton = this.addButton(this.profileButton.x + 70 * CU.S, this.profileButton.y + 5 * CU.S,
            CU.shiftLeft(104), CU.shiftTop(31),
            mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('magazin'),
            Keys.ATLAS_UI_2, Keys.UI2_SHOP, this.shopButtonContainer)[0];

        this.bankButton = this.addButton(this.shopButton.x + 66 * CU.S, this.profileButton.y + 5 * CU.S,
            CU.shiftLeft(170), CU.shiftTop(31),
            mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('bank'),
            Keys.ATLAS_UI_2, Keys.UI2_BANK, this.bankButtonContainer)[0];

        this.tasksButton = this.addButton(CU.shiftRight(34 + p.getButtonsRightX()), CU.shiftTop(34 + p.getButtonsTopY()),
            CU.shiftRight(34), CU.shiftTop(34),
            mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('zadanija'),
            Keys.ATLAS_UI_2, Keys.UI2_TASKS, this.tasksButtonContainer, 40)[0];

        this.forumButton = this.addButton( this.tasksButton.x + 2 * CU.S, this.tasksButton.y + (80 + sideRibbonsCount * 60) * CU.S,
            CU.shiftLeft(32), this.tasksButton.y + 80 * CU.S,
            mode, MenuColumn.RIGHT, MenuColumn.OTHER, Tr('frm').toUpperCase(), Keys.ATLAS_UI_2, Keys.UI2_FORUM, this.forumButtonContainer)[0];
        this.forumSign = new MenuNotification(this.scene!, this.forumButton.x, this.forumButton.y);
        this.forumButtonContainer.add(this.forumSign);


        let yy = this.forumButton.y;

        this.arenaButton = this.addButton(this.canvas!.width - 32 * CU.S, yy + 75 * CU.S,
            CU.shiftLeft(32), this.forumButton.y + 75 * CU.S + (player.accessLevel === AccessLevel.TRIAL ? 50 * CU.S : 0),
            mode, MenuColumn.RIGHT, MenuColumn.OTHER, Tr('arena'), Keys.ATLAS_UI_2, player.level < Settings.ARENA_MIN_LEVEL ? Keys.UI2_ARENA_GR : Keys.UI2_ARENA,
            this.arenaButtonContainer)[0];
        this.arenaSign = new MenuNotification(this.scene!, this.arenaButton.x, this.arenaButton.y);
        this.arenaButtonContainer.add(this.arenaSign);
        this.arenaButtonContainer.setVisible(player.level >= Settings.ARENA_MIN_LEVEL_TO_SHOW_BUTTON);
        if (this.arenaButtonContainer.visible) {
            yy = this.arenaButton.y;
        }

        this.clanButton = this.addButton(this.canvas!.width - 32 * CU.S, yy + 75 * CU.S,
            CU.shiftLeft(32), this.arenaButton.y + 75 * CU.S + (player.accessLevel === AccessLevel.TRIAL ? 50 * CU.S :0),
            mode, MenuColumn.RIGHT, MenuColumn.OTHER, Tr('klan'), Keys.ATLAS_UI_2,
            player.level < Settings.CLAN_MIN_LEVEL ? Keys.UI2_CLAN_GR : Keys.UI2_CLAN, this.clanButtonContainer)[0];
        this.clanSign = new MenuNotification(this.scene!, this.clanButton.x, this.clanButton.y);
        this.clanButtonContainer.add(this.clanSign);
        this.clanButtonContainer.setVisible(player.level >= Settings.CLAN_MIN_LEVEL_TO_SHOW_BUTTON);
        if (this.clanButtonContainer.visible) {
            yy = this.clanButton.y;
        }

        let yyLeftModern = 280 * CU.S + (GameState.showSulfur ? 45 * CU.S : 0) + (GameState.showCaviar ? 45 * CU.S : 0);
        let yyLeftOrig = 340 * CU.S;
        let chainCnt = 0;
        if (player.chains && player.chains.length > 0) {

            for (let t of this.chainTimerIds) {
                clearInterval(t);
            }
            this.chainTimerIds = [];

            this.chainSigns = [];
            for (let chain of MenuService.getSortedChains(player)) {

                let remainSec = Math.floor((chain!.expiresAt - TimeUtils.getServerTimeMs()) / 1000);

                if (remainSec < 0 && chain.chainState !== ChainState.CONGRATULATION && chain.chainState !== ChainState.COMPLETED) {
                    continue;
                }

                remainSec = Math.max(remainSec, 0);

                let chainContainer: Container = this.createContainer(scene);
                this.chainContainers.push(chainContainer);

                let [chainButton, chainText] = this.addButton( 36 * CU.S, yyLeftModern + chainCnt * 75 * CU.S,
                    CU.shiftLeft(32), yyLeftOrig + chainCnt * 75 * CU.S,
                    mode, MenuColumn.OTHER, MenuColumn.OTHER, TimeUtils.getDurationText(remainSec), Keys.ATLAS_CHAIN, ChainUtils.toMenuImageId(chain.chainId), chainContainer);
                this.chainButtons.push(chainButton);
                yy = chainButton.y;

                chainButton.setInteractive().on('pointerup', () => this.openChainCallback(chain.id, chainContainer), chainButton);

                if (chain.chainState === ChainState.CONGRATULATION || chain.chainState === ChainState.COMPLETED) {
                    chainText.setText(Tr('compl'));
                    chainText.setColor('#5dff50');
                } else {
                    let chainTimerId = setInterval(() => {
                        if (chainButton && chainButton.active && this.scene!.scene.isActive()) {
                            let remainSec = Math.floor((chain!.expiresAt - TimeUtils.getServerTimeMs()) / 1000);
                            remainSec = Math.max(remainSec, 0);
                            let durText = TimeUtils.getDurationText(remainSec);
                            chainText.setText(durText);

                            if (remainSec <= 0) {
                                clearInterval(chainTimerId);
                                chainContainer.setVisible(false)
                                chainContainer.destroy();
                            }
                        } else {
                            clearInterval(chainTimerId);
                        }

                    }, 1000);
                }

                ++chainCnt;

                let chainSign = new MenuNotification(this.scene!, chainButton.x, chainButton.y);
                chainContainer.add(chainSign);
                this.chainSigns.push(chainSign)
                if (chain.chainState === ChainState.TAKE_REWARD) {
                    chainSign.show(0);
                } else if (chain.chainState === ChainState.TIZER) {
                    ChainStore.getTizer(chain.id).then((hint: HintDto) => {
                        let ip: TizerPanel = new TizerPanel(this.scene!, this.canvas!, hint, (show: boolean) => {
                            ip.close();
                            if (show) {
                                this.openChainCallback(chain.id, chainContainer)
                            }
                            ChainStore.setSeen(chain.id);
                        });
                        ip.openAnimated();
                    });
                } else if (chain.chainState === ChainState.CONGRATULATION) {
                    ChainStore.getCongratulation(chain.id).then((hint: HintDto) => {
                        let ip: TizerPanel = new TizerPanel(this.scene!, this.canvas!, hint, (show: boolean) => {
                            ip.close();
                            if (show) {
                                this.openBankCallback();
                            }
                            chainContainer.destroy();
                            ChainStore.setSeen(chain.id);
                        });
                        ip.openAnimated();
                    });
                }
            }
        }

        let sale: Sale | undefined = PlayerStore.GET_SALE();
        if (!!sale) {
            let sbm = SaleUtils.getButtonMode(sale);
            let saleText: Text;
            [this.saleButton, saleText] = this.addButton(36 * CU.S, yyLeftModern + chainCnt * 75 * CU.S,
                CU.shiftLeft(32), yyLeftOrig + chainCnt * 75 * CU.S,
                mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('b.fr'), Keys.ATLAS_CHAIN, 'fire', this.saleButtonContainer);
            this.saleButtonContainer.setVisible(!!sale);

            let timerBefore: number | undefined = undefined;
            let timerPrefix: string | undefined = undefined;
            switch (sbm) {
                case SaleButtonMode.TIMER_TILL_START:
                    timerBefore = sale!.startAt;
                    timerPrefix = Tr('bf.ts') + ' ';
                    break;
                case SaleButtonMode.ACTIVE:
                    if (saleText && saleText.scene.scene.isActive()) {
                        saleText.setText(Tr('b.fr').toUpperCase());
                        saleText.setColor('#91ff57');
                    }
                    break;
                case SaleButtonMode.TIMER_TILL_END:
                    timerBefore = sale!.endAt;
                    timerPrefix = Tr('do.kontsa') + ' ';
                    break;
            }
            timerPrefix = '';

            if (!!timerBefore) {
                let self = this;
                let saleTimerId = setInterval(() => {
                    if (this.saleButton && this.saleButton.active && this.scene!.scene.isActive()) {
                        let remainSec = Math.floor((timerBefore! - TimeUtils.getServerTimeMs()) / 1000);
                        remainSec = Math.max(remainSec, 0);
                        let durText = timerPrefix + TimeUtils.getDurationText(remainSec);
                        if (saleText && saleText.scene && saleText.scene.scene.isActive()) {
                            saleText.setText(durText);

                            if (remainSec <= 0) {
                                switch (sbm) {
                                    case SaleButtonMode.TIMER_TILL_START:
                                        saleText.setText(Tr('b.fr').toUpperCase());
                                        saleText.setColor('#91ff57');
                                        break;
                                    case SaleButtonMode.ACTIVE:
                                        if (saleText && saleText.scene.scene.isActive()) {
                                            saleText.setText(Tr('b.fr').toUpperCase());
                                            saleText.setColor('#91ff57');
                                        }
                                        break;
                                    case SaleButtonMode.TIMER_TILL_END:
                                        clearInterval(saleTimerId);
                                        self.saleButtonContainer.setVisible(false)
                                        self.saleButtonContainer.destroy();
                                        break;
                                }
                            }
                        } else {
                            clearInterval(saleTimerId);
                        }
                    } else {
                        clearInterval(saleTimerId);
                    }
                }, 1000);
            }
        }

        this.hintButton = this.addButton(this.canvas!.width - 32 * CU.S, this.clanButton.y + 75 * CU.S,
            CU.shiftLeft(32), this.clanButton.y + 75 * CU.S + (player.accessLevel === AccessLevel.TRIAL ? 50 * CU.S :0),
            mode, MenuColumn.RIGHT, MenuColumn.OTHER, Tr('hint'), undefined,
            Keys.HINT_BUTTON, this.hintButtonContainer)[0];
        this.hintSign = new MenuNotification(this.scene!, this.hintButton.x, this.hintButton.y);
        this.hintButtonContainer.add(this.hintSign);
        this.hintButtonContainer.setVisible(TaskService.showHint(player));

        this.powerUpPanel = new PowerUpPanel(scene, 20 * CU.S, this.profileButton.y - 75 * CU.S, player);

        this.leaderboardButton = this.addButton(CU.shiftLeft(142), CU.shiftTop(31 + p.getButtonsTopY()),
            CU.shiftRight(32), this.tasksButton.y + (80 + sideRibbonsCount * 60) * CU.S,
            mode, MenuColumn.OTHER, MenuColumn.RIGHT, Tr('reyting').toUpperCase(),
            Keys.ATLAS_UI_2, Keys.UI2_RATING, this.leaderboardButtonContainer)[0];

        this.mailButton = this.addButton(this.tasksButton.x - 70 * CU.S, this.tasksButton.y - 4 * CU.S,
            this.tasksButton.x - 70 * CU.S, 31 * CU.S,
            mode, MenuColumn.OTHER, MenuColumn.OTHER, PlayerStore.isAdmined() ? Tr('vihod') : Tr('pochta'),
            Keys.ATLAS_UI_2, PlayerStore.isAdmined() ? Keys.UI2_PROFILE : Keys.UI2_MAIL, this.mailButtonContainer)[0];

        this.chatButton = this.addButton(this.mailButton.x - 66 * CU.S, this.tasksButton.y - 4 * CU.S,
            this.mailButton.x - 66 * CU.S, 31 * CU.S,
            mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('chat'),
            Keys.ATLAS_UI_2, Keys.UI2_CHAT, this.chatButtonContainer)[0];

        let askReload = PlayerStore.getVersion() > Version.VERSION_NUM
            && PlayerStore.getVersion() > player.reloadRequestedVersion
            && process.env.VUE_APP_IS_MOBILE_APP !== 'true';

        this.reloadButton = this.addButton(this.canvas!.width - 30, this.canvas!.height - 142,
            this.canvas!.width - 30, this.canvas!.height - 142,
            mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('v.o.ttl'),
            Keys.ATLAS_PROFILE, Keys.UI_INFO, this.reloadButtonContainer)[0];
        this.reloadButtonContainer.setVisible(askReload);

        if (IntegrationUtils.isYandexUser() && player.level + 3 < YaStorage.getMaxLevel() && !YaStorage.isChooseRejected()) {
            this.yaRemapButton = this.addButton(this.canvas!.width - 30, this.canvas!.height - 192,
                this.canvas!.width - 30, this.canvas!.height - 192,
                mode, MenuColumn.OTHER, MenuColumn.OTHER, Tr('acc'),
                Keys.ATLAS_PROFILE, Keys.UI_INFO, this.yaRemapContainer)[0];
        }

        this.newBankSign = new MenuNotification(this.scene!, this.bankButton.x,  this.bankButton.y);
        this.newTaskSign = new MenuNotification(this.scene!, this.tasksButton.x,  this.tasksButton.y);
        this.newBadgeSign = new MenuNotification(this.scene!, this.profileButton.x, this.profileButton.y);
        this.newMailSign = new MenuNotification(this.scene!, this.mailButton.x, this.mailButton.y);
        this.newChatSign = new MenuNotification(this.scene!, this.chatButton.x, this.chatButton.y);

        ListenerManager.setChatSeenCallback((seen: boolean) => {
            this.setSignVisibility(this.newChatSign, seen);
        })

        this.taskDoneIcon = SpriteUtils.addSprite(this.scene!, 546, 49, Keys.ATLAS_PROFILE, Keys.UI_CHECK_SIGN);
        this.taskDoneIcon.setVisible(player.tasksAvailable && player.haveCompletedTasks);

        if (player.haveNewTasks) {
            this.newTaskSign.show(0);
        }

        if (player.haveBankNews) {
            this.newBankSign.show(0);
        }

        if (player.haveNewBadges) {
            this.newBadgeSign.show(0);
        }

        if (player.haveNewMailMessage) {
            this.newMailSign.show(0);
        }

        if (MenuService.promptArena(player)) {
            this.arenaSign.show(0);
        }

        if (ClanUtils.promptClan(player)) {
            this.clanSign.show(0);
        }

        if (TaskService.promptHint(player)){
            this.hintSign.show(0);
        }

        this.bankButtonContainer.add(this.newBankSign);
        this.tasksButtonContainer.add([this.taskDoneIcon, this.newTaskSign]);
        this.profileButtonContainer.add(this.newBadgeSign);
        this.mailButtonContainer.add(this.newMailSign);
        this.chatButtonContainer.add(this.newChatSign);

        this.profileButton.setInteractive().on('pointerdown', () => this.openProfileCallback(), this.profileButton);
        this.shopButton.setInteractive().on('pointerup', () => this.openShopCallback(), this.shopButton);
        this.bankButton.setInteractive().on('pointerup', () => {
            this.setSignVisibility(this.newBankSign, false);
            this.openBankCallback();
            }, this.bankButton);
        this.arenaButton.setInteractive().on('pointerup', () => this.openArenaCallback(), this.arenaButton);
        this.hintButton.setInteractive().on('pointerup', () => {
            this.setSignVisibility(this.hintSign, false);
            this.openHelpCallback();
        }, this.hintButton);
        this.clanButton.setInteractive().on('pointerup', () => {
            this.setSignVisibility(this.clanSign, false);
            this.openClanCallback();
        }, this.clanButton);
        this.leaderboardButton.setInteractive().on('pointerup', () => this.openRatingPage(), this.leaderboardButton);
        this.chatButton.setInteractive().on('pointerup', () => this.openChatPage(), this.chatButton);
        this.reloadButton.setInteractive().on('pointerup', () => this.openReloadDialog(), this.reloadButton);
        this.mailButton.setInteractive().on('pointerup', () => this.openMailPage(), this.mailButton);
        this.tasksButton.setInteractive().on('pointerdown',
            () => this.openTasksCallback(),  this.tasksButton);

        if (!!this.saleButton) {
            this.saleButton.setInteractive().on('pointerup', () => this.openSaleCallback(PlayerStore.GET_SALE()!.saleId), this.saleButton);
        }

        if (!!this.yaRemapButton) {
            this.yaRemapButton.setInteractive().on('pointerup', () => this.openYaAccCallback(this.yaRemapContainer), this.yaRemapButton);
        }

        this.forumButton.setInteractive().on('pointerup', () => this.openForumCallback(), this.forumButton);

        if (player.accessLevel === AccessLevel.TRIAL && !IntegrationUtils.isKongUser()) {
            this.registerButtonRibbon = SpriteUtils.addSpriteN(this.scene!, CU.shiftRight(86), CU.shiftTop(119),
                Keys.ATLAS_UI, Keys.UI_SCENARIO_RIBBON, -1, 1);
            let registerButtonText = TextUtils.addTextAdvanced(this.scene!, Tr('registratsija'),
                this.registerButtonRibbon.x / CU.S + 8, this.registerButtonRibbon.y / CU.S - 9,
                '#e8eee2', 10, 'normal', 400, 'center', 2, Fonts.ARIAL_MT);

            this.registerButtonContainer.add([this.registerButtonRibbon, registerButtonText]);
            this.registerButtonRibbon.setInteractive().on('pointerup', this.openRegisterPage, this.registerButtonRibbon);
        } else {
            this.registerButtonRibbon = undefined;
        }

    }

    public setMenuButtonsInteractions(disableInteractions: boolean) {
        if (disableInteractions) {

            this.profileButton.disableInteractive();
            this.shopButton.disableInteractive();
            this.bankButton.disableInteractive();
            this.arenaButton.disableInteractive();
            this.clanButton.disableInteractive();
            this.forumButton.disableInteractive();
            this.leaderboardButton.disableInteractive();
            this.chatButton.disableInteractive();
            this.reloadButton.disableInteractive();
            this.mailButton.disableInteractive();
            this.tasksButton.disableInteractive();
            if (this.yaRemapButton) {
                this.yaRemapButton.disableInteractive();
            }
            if (this.registerButtonRibbon) { this.registerButtonRibbon.disableInteractive(); }


        } else {

            this.profileButton.setInteractive();
            this.shopButton.setInteractive();
            this.bankButton.setInteractive();
            this.arenaButton.setInteractive();
            this.clanButton.setInteractive();
            this.forumButton.setInteractive();
            this.leaderboardButton.setInteractive();
            this.chatButton.setInteractive();
            this.reloadButton.setInteractive();
            this.mailButton.setInteractive();
            this.tasksButton.setInteractive();
            if (this.registerButtonRibbon) {
                this.registerButtonRibbon.setInteractive();
            }

            if (this.yaRemapButton) {
                this.yaRemapButton.setInteractive();
            }
        }
    }

    public openMailPage(): void {
        if (PlayerStore.isAdmined()) {
            PlayerStore.techExit().then(() => {
                window.location.reload();
            });
        } else {
            CU.blockCanvas(SceneUtils.getCurrentScene());
            ListenerManager.callMailCallback();
        }
    }

    public setShowNavPanel(show: boolean) {
        this.showNavPanel = show;
    }

    public onBalanceChange(): void {
        if (this.scene!.scene.isActive()) {
            const player: Player = PlayerStore.getPlayer();
            this.resContainer!.setBalance(player);
        }
    }

    private newHeroAnimated = false;

    public updateHint(): void {
        if (this.scene && this.scene.scene.isActive()) {
            this.setButtonContainerVisibility(this.hintButtonContainer, TaskService.showHint(PlayerStore.getPlayer()))
            this.setSignVisibility(this.hintSign, TaskService.promptHint(PlayerStore.getPlayer()));
        }
    }

    public onChange(): void {
        if (this.scene!.scene.isActive()) {
            const self = this;
            const player: Player = PlayerStore.getPlayer();
            let creatures: Creature[] = CreatureUtils.sortCreaturesDescending(player.creatures);
            // Делаю массив постоянных героев
            this.nonTemporalCreaturesList = CreatureUtils.findNonTemporalCreatures(creatures)
            // Ищу в массиве !temporal первое значение
            this.firstNonTemporalCreaturePos = this.nonTemporalCreaturesList.findIndex(c => !c.temporal);
            // Создаю панели для всех созданий
            this.resContainer!.setBalance(player);

            if (!!player.chains) {
                let i = 0;
                for (let chain of MenuService.getSortedChains(player)) {
                    if (!!this.chainSigns && i < this.chainSigns.length) {
                        let chainSign = this.chainSigns[i];
                        if (!!chainSign && chainSign.active) {
                            if (chain.chainState === ChainState.TAKE_REWARD) {
                                chainSign.show(0);
                            } else {
                                chainSign.setShown(false);
                                chainSign.setVisible(false);
                            }
                        }
                    }
                    ++i;
                }
            }

            if (this.withCreatures) {
                this.showCreatureSkillInteractionEffect(creatures);
                // Если +1, то показываем мега-анимацию
                if (this.creaturePanelById.size !== creatures.length || GameState.REQUEST_REFRESH_NAV_PANEL) {

                    let recreateNavPanel = () => {
                        GameState.REQUEST_REFRESH_NAV_PANEL = false;
                        for (let cp of this.creaturePanelById.values()) {
                            cp.destroy();
                        }
                        this.creaturePanelById.clear();
                        this.addCreaturesNavPanel(creatures);
                    };
                    if (creatures.length === 2 && this.creaturePanelById.size === 1) {
                        if (!this.newHeroAnimated) {
                            const CREATURES = PlayerStore.getCombativeCreatures();
                            (this.scene as RoadScene)?.createHeroJoiningAnim(PlayerUtils.getGender(player) ? Keys.BIG_CAT_MALE : Keys.BIG_CAT_FEMALE,
                                StringUtils.capitalizeFirstLetter(Tr('noviy.geroy').toLowerCase()), CREATURES.length - 1);
                            this.newHeroAnimated = true;
                        }
                        let timerId = setInterval(() => {
                            clearInterval(timerId);
                            recreateNavPanel();
                        }, 6600);
                    } else {
                        recreateNavPanel();
                    }
                } else {
                    creatures.forEach(c => {
                        let cp: CreaturePanel | undefined = self.creaturePanelById.get(c.id);
                        if (cp) {
                            cp.setSkills(c.skills, false);
                        }
                    });
                }
            }

            this.taskDoneIcon.setVisible(player.tasksAvailable && player.haveCompletedTasks);

            this.setSignVisibility(this.newBankSign, player.haveBankNews);
            this.setSignVisibility(this.newTaskSign, player.haveNewTasks);
            this.setSignVisibility(this.newBadgeSign, player.haveNewBadges);
            this.setSignVisibility(this.newMailSign, player.haveNewMailMessage);
            this.setSignVisibility(this.hintSign, TaskService.promptHint(player));
            this.setSignVisibility(this.arenaSign, MenuService.promptArena(player));
            this.setSignVisibility(this.clanSign, ClanUtils.promptClan(player));

            this.setButtonContainerVisibility(this.hintButtonContainer, TaskService.showHint(player))
            this.updatePowerUpPanel(player);

            if (player.newTaskText) {
                new NewTaskFeedback(this.scene!, player.newTaskText).show();
                TaskStore.setNewTaskSeen();
            }

            this.setupHeroPanelDepth();
        }
    }

    public bringFooterToTop() {
        this.resourceToBottom();
        this.mainContainer!.setDepth(4000);
        this.setupHeroPanelDepth();
    }

    public bringFooterToBottom() {
        this.resourceToSide();
        this.mainContainer!.setDepth(1000);
        this.setupHeroPanelDepth();
    }

    private setupHeroPanelDepth() {
        if (!this.showNavPanel) {
            this.menuNavPanel.setDepth(-1);
        } else {
            this.menuNavPanel.setDepth(this.mainContainer.depth + 1);
        }
    }

    private showCreatureSkillInteractionEffect(creatures: Creature[]) {
        this.creaturePanelById.forEach(panel => {
            let prevCreature = panel.getCreature();
            let newCreature = creatures.find(c => c.id == prevCreature.id);
            if (newCreature) {
                prevCreature.skills.forEach(pcs => {
                    let currentSkill = newCreature!.skills.find(ncs => ncs.skillType == pcs.skillType);
                    if (currentSkill && currentSkill.valueTotal > pcs.valueTotal) {
                        let y = this.menuNavPanel.y / CU.S;
                        let x = this.menuNavPanel.x / CU.S + panel.x / CU.S;
                        let skillDeltaText = "+" + TextUtils.formatMoney(currentSkill.valueTotal - pcs.valueTotal);
                        let toAlpha = 0;
                        let skillEffect: CreatureInteractionEffectTypePanel = new CreatureInteractionEffectTypePanel(this.scene!, x, y, skillDeltaText, pcs.skillType,
                            panel.depth + 1, toAlpha, 30, 50, 0, () => skillEffect.destroy());
                        pcs.valueCurrent = currentSkill.valueCurrent;
                        pcs.valueTotal = currentSkill.valueTotal;
                        this.scene!.add.existing(skillEffect);
                    }
                })
            }
        })
    }

    public showCreatureSkillEffect(skillType: SkillType, delta: number, cId: number | undefined = undefined) {
        this.creaturePanelById.forEach(panel => {
            let prevCreature = panel.getCreature();
            if (!cId || cId === prevCreature.id) {
                prevCreature.skills.forEach(pcs => {
                    if (pcs.skillType === skillType) {
                        let y = this.menuNavPanel.y / CU.S;
                        let x = this.menuNavPanel.x / CU.S + panel.x / CU.S;
                        let skillDeltaText = "+" + TextUtils.formatMoney(delta);
                        let toAlpha = 0;
                        let skillEffect: CreatureInteractionEffectTypePanel = new CreatureInteractionEffectTypePanel(this.scene!, x, y, skillDeltaText, pcs.skillType,
                            panel.depth + 1, toAlpha, 30, 50, 0, () => skillEffect.destroy());
                        pcs.valueCurrent += delta;
                        pcs.valueTotal += delta;
                        this.scene!.add.existing(skillEffect);
                        panel.setSkill(pcs, false);
                    }
                });
            }
        })
    }

    public showResourcesAddedEffect(resourceType: ResourceType, delta: number) {
        if (this.resPanel) {
            this.resPanel!.panelByRes.forEach((panel, rt: ResourceType) => {
                if (rt === resourceType) {
                    let y = this.resPanel!.y / CU.S + panel.parentContainer.y / CU.S + 20;
                    let x = this.resPanel!.x / CU.S + panel.parentContainer.x / CU.S + 110;

                    let skillDeltaText = "+" + TextUtils.formatMoney(delta);
                    let toAlpha = 0;
                    let resourceEffect: ResourceAddEffectPanel = new ResourceAddEffectPanel(this.scene!, x, y, skillDeltaText, rt,
                        panel.depth + 1, toAlpha, 0, -30, 0, () => resourceEffect.destroy());
                    panel.setBalance(panel.getBalance() + delta);
                    this.scene!.add.existing(resourceEffect);
                }
            });
        }
    }


 public updatePowerUpPanel(player:Player){
        let scene = this.scene
        let allItems = PowerUpUtils.getItems();
        let items = allItems.slice(0, PowerUpPanel.MAX_ITEMS);

        //проверяем, изменился ли список предметов
        if(!this.stringArraysEquals(items.map(m => m.marker), this.powerUpPanel.shownPowerUpMarkers)){
            this.powerUpPanel.destroy();
            this.powerUpPanel = new PowerUpPanel(scene!, 20 * CU.S, this.profileButton.y - 75 * CU.S, player);
        }
    }

    private stringArraysEquals(arr1: string[], arr2: string[]): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }

        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }

        return true;
    }
    private setSignVisibility(sign: MenuNotification, haveNew: boolean) {
        if (haveNew) {
            sign.setVisible(true);
            sign.show(5000);
        } else {
            sign.setShown(false);
            sign.setVisible(false);
        }
    }

    private setButtonContainerVisibility(container:Container, visible:boolean){
        container.visible = visible;
    }

    private openRegisterPage(): void {
        CU.blockCanvas(this.scene!);
        ListenerManager.callRegisterCallback();
    }

    private openRatingPage(): void {
        CU.blockCanvas(this.scene!);
        ListenerManager.callRatingCallback();
    }

    private openClanRatingPage(): void {
        CU.blockCanvas(this.scene!);
        ListenerManager.callClanRatingCallback();
    }

    private openChatPage(): void {
        CU.blockCanvas(this.scene!);
        ListenerManager.callChatOpenCallback(Settings.MAIN_CHAT_ROOM);
    }

    private openReloadDialog(): void {
        let d = new InfoPanel(this.scene!, this.canvas!, Tr('v.o.ttl'), Tr('v.o.int'),
            Tr('v.o.body'), () => {
                PlayerStore.updateReloadVersion().then(() => {
                    window.location.replace(window.location.href);
                });
            }, Tr('obnovit'));
        d.openAnimated();
    }

    private static promptArena(player: Player): boolean {
        if (player.level < Settings.ARENA_MIN_LEVEL) {
            return false;
        }
        return !!player.arenaOpensAt && player.arenaOpensAt <= TimeUtils.getServerTimeMs();
    }

    private static getSortedChains(player: Player): Chain[] {
        if (!player.chains) {
            return [];
        }
        return Object.assign([], player.chains).sort((a: Chain, b: Chain) => {
            return a.id - b.id;
        });
    }
}

class ButtPos {
    public name!: string;
    public x!: number;
    public y!: number;
}
